import { AutoComplete, Checkbox, Input, Modal } from "@arco-design/web-react";
import { useEffect, useState } from "react";

import { sysDeptStatusOptions } from "../config";
import { createEmptySysStaffMutation, type SysDeptOption } from "../service/sysDeptUserService";
import type { SysStaffItem, SysStaffMutation } from "../types";
import "./SysDeptUserPage.css";

type SysStaffMutationModalProps = {
  canGrantSuperuser: boolean;
  state:
    | { type: "create"; selectedDeptId?: number }
    | { type: "edit"; staff: SysStaffItem }
    | null;
  deptOptions: SysDeptOption[];
  errorMessage: string;
  onClose: () => void;
  onClearError: () => void;
  onCreateStaff: (payload: SysStaffMutation) => Promise<void>;
  onUpdateStaff: (userId: number, payload: SysStaffMutation) => Promise<void>;
};

/** 用户编辑弹窗。 */
export function SysStaffMutationModal({
  canGrantSuperuser,
  state,
  deptOptions,
  errorMessage,
  onClose,
  onClearError,
  onCreateStaff,
  onUpdateStaff,
}: SysStaffMutationModalProps) {
  const [payload, setPayload] = useState<SysStaffMutation>(createEmptySysStaffMutation());
  const [mainDeptText, setMainDeptText] = useState("");
  const deptAutoCompleteOptions = deptOptions.map((option) => ({
    value: String(option.value),
    name: option.label,
  }));

  useEffect(() => {
    if (!state) {
      return;
    }
    onClearError();
    if (state.type === "create") {
      const nextPayload = createEmptySysStaffMutation(state.selectedDeptId);
      setPayload(nextPayload);
      setMainDeptText(getDeptOptionLabel(deptOptions, nextPayload.mainDeptId));
      return;
    }
    const nextPayload = {
      username: state.staff.username,
      realname: state.staff.realname,
      nickname: state.staff.nickname,
      phone: state.staff.phone,
      email: state.staff.email,
      avatar: state.staff.avatar,
      sex: state.staff.sex,
      mainDeptId: state.staff.mainDeptId,
      deptIds: state.staff.deptIds,
      status: state.staff.status,
      userType: state.staff.userType,
      supplierId: state.staff.supplierId,
      wxOpenid: state.staff.wxOpenid,
      wxUnionid: state.staff.wxUnionid,
      isSubscribe: state.staff.isSubscribe,
      isSuperuser: state.staff.isSuperuser,
      isStaff: state.staff.isStaff,
      supervisors: state.staff.supervisors.map((item) => ({
        supervisorId: item.supervisorId,
        supervisorLevel: item.supervisorLevel,
        orderNum: item.orderNum,
      })),
    };
    setPayload(nextPayload);
    setMainDeptText(getDeptOptionLabel(deptOptions, nextPayload.mainDeptId) || state.staff.mainDeptName || "");
  }, [state, deptOptions, onClearError]);

  /** 提交用户新增或编辑。 */
  async function handleOk() {
    if (!state) {
      return;
    }
    const mutation = canGrantSuperuser
      ? payload
      : { ...payload, isSuperuser: undefined };
    if (state.type === "create") {
      await onCreateStaff(mutation);
    } else {
      await onUpdateStaff(state.staff.id, mutation);
    }
    onClose();
  }

  return (
    <Modal
      title={state?.type === "edit" ? "编辑用户" : "新增用户"}
      visible={Boolean(state)}
      onCancel={onClose}
      onOk={handleOk}
      okText="保存"
      cancelText="取消"
      className="sys-dept-user-modal"
      unmountOnExit
    >
      {errorMessage ? <div className="sys-dept-user-error">{errorMessage}</div> : null}
      <div className="sys-dept-user-modal-grid">
        <label className="sys-dept-user-field">
          <span>账号</span>
          <Input
            aria-label="账号"
            value={payload.username}
            onChange={(username) => setPayload({ ...payload, username })}
          />
        </label>
        <label className="sys-dept-user-field">
          <span>姓名</span>
          <Input
            aria-label="姓名"
            value={payload.realname}
            onChange={(realname) => setPayload({ ...payload, realname })}
          />
        </label>
        <label className="sys-dept-user-field">
          <span>昵称</span>
          <Input
            aria-label="昵称"
            value={payload.nickname}
            onChange={(nickname) => setPayload({ ...payload, nickname })}
          />
        </label>
        <label className="sys-dept-user-field">
          <span>手机号</span>
          <Input
            aria-label="手机号"
            value={payload.phone}
            onChange={(phone) => setPayload({ ...payload, phone })}
          />
        </label>
        <label className="sys-dept-user-field">
          <span>邮箱</span>
          <Input
            aria-label="邮箱"
            value={payload.email}
            onChange={(email) => setPayload({ ...payload, email })}
          />
        </label>
        <label className="sys-dept-user-field">
          <span>头像 URL</span>
          <Input
            aria-label="头像 URL"
            value={payload.avatar}
            onChange={(avatar) => setPayload({ ...payload, avatar })}
          />
        </label>
        <label className="sys-dept-user-field">
          <span>性别</span>
          <select
            aria-label="性别"
            className="sys-dept-user-native-select"
            value={payload.sex ?? 2}
            onChange={(event) => setPayload({ ...payload, sex: Number(event.target.value) })}
          >
            <option value={0}>女</option>
            <option value={1}>男</option>
            <option value={2}>未知</option>
          </select>
        </label>
        <label className="sys-dept-user-field">
          <span>主部门</span>
          <AutoComplete
            allowClear
            data={deptAutoCompleteOptions}
            filterOption={filterAutoCompleteOption}
            placeholder="请选择主部门"
            value={mainDeptText}
            inputProps={{ "aria-label": "主部门" }}
            onChange={(value) => {
              setMainDeptText(value);
              if (!value) {
                setPayload({ ...payload, mainDeptId: undefined });
              }
            }}
            onSelect={(value) => {
              setMainDeptText(getDeptOptionLabel(deptOptions, Number(value)));
              setPayload({ ...payload, mainDeptId: Number(value) });
            }}
          />
        </label>
        <label className="sys-dept-user-field">
          <span>归属部门</span>
          <select
            aria-label="归属部门"
            className="sys-dept-user-multiple-select"
            multiple
            value={payload.deptIds.map(String)}
            onChange={(event) =>
              setPayload({
                ...payload,
                deptIds: Array.from(event.target.selectedOptions).map((option) => Number(option.value)),
              })
            }
          >
            {deptOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="sys-dept-user-field">
          <span>用户类型</span>
          <select
            aria-label="用户类型"
            className="sys-dept-user-native-select"
            value={payload.userType}
            onChange={(event) => setPayload({ ...payload, userType: Number(event.target.value) })}
          >
            <option value={1}>内部用户</option>
            <option value={2}>供应商用户</option>
          </select>
        </label>
        <label className="sys-dept-user-field">
          <span>供应商 ID</span>
          <Input
            aria-label="供应商 ID"
            value={payload.supplierId ? String(payload.supplierId) : ""}
            onChange={(value) => setPayload({ ...payload, supplierId: value ? Number(value) : undefined })}
          />
        </label>
        <label className="sys-dept-user-field">
          <span>微信 OpenID</span>
          <Input
            aria-label="微信 OpenID"
            value={payload.wxOpenid}
            onChange={(wxOpenid) => setPayload({ ...payload, wxOpenid })}
          />
        </label>
        <label className="sys-dept-user-field">
          <span>微信 UnionID</span>
          <Input
            aria-label="微信 UnionID"
            value={payload.wxUnionid}
            onChange={(wxUnionid) => setPayload({ ...payload, wxUnionid })}
          />
        </label>
        <label className="sys-dept-user-field">
          <span>状态</span>
          <select
            aria-label="员工状态"
            className="sys-dept-user-native-select"
            value={payload.status}
            onChange={(event) => setPayload({ ...payload, status: Number(event.target.value) })}
          >
            {sysDeptStatusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="sys-dept-user-switch-field">
          <span>用户身份</span>
          <Checkbox
            aria-label="是否员工"
            checked={payload.isStaff}
            onChange={(checked) => setPayload({ ...payload, isStaff: checked })}
          >
            是否员工
          </Checkbox>
        </label>
        <label className="sys-dept-user-switch-field">
          <span>权限标记</span>
          <Checkbox
            aria-label="是否超级管理员"
            checked={Boolean(payload.isSuperuser)}
            disabled={!canGrantSuperuser}
            onChange={(checked) => setPayload({ ...payload, isSuperuser: checked })}
          >
            超级管理员
          </Checkbox>
        </label>
        <label className="sys-dept-user-switch-field">
          <span>消息订阅</span>
          <Checkbox
            aria-label="是否订阅消息"
            checked={payload.isSubscribe}
            onChange={(checked) => setPayload({ ...payload, isSubscribe: checked })}
          >
            订阅消息
          </Checkbox>
        </label>
      </div>
    </Modal>
  );
}

/** 按展示名和 ID 过滤自动补全选项。 */
function filterAutoCompleteOption(inputValue: string, option: { value?: string | number; extra?: { name?: string } }) {
  const keyword = inputValue.trim().toLowerCase();
  if (!keyword) {
    return true;
  }
  const text = `${option.extra?.name ?? ""} ${option.value ?? ""}`.toLowerCase();
  return text.includes(keyword);
}

/** 根据部门 ID 获取自动补全展示名称。 */
function getDeptOptionLabel(options: SysDeptOption[], value?: number): string {
  if (!value) {
    return "";
  }
  return options.find((option) => option.value === value)?.label ?? "";
}
